import { PdfImportController } from '@/controllers/admin/pdfImport'
import {
  BomViewResponse,
  BomItemDto,
  BomRowState,
  BomSaveRequest,
  BomSaveItemDto,
  PartLookupResponse
} from '@/interfaces/admin/pdfImport'
import { Module } from 'vuex'
import { postSuccess, postError } from '@/helpers/notification'
import i18n from '@/locales'
import { debounce } from 'lodash'

export interface PdfImportPageDetailsBomState {
  bom: BomViewResponse | null
  bomRows: BomRowState[]
  highlightedRowKey: string | null
  importId: number | null
  loading: boolean
  originalBomRows: BomItemDto[]
  pageId: number | null
  saving: boolean
}

const initialState: PdfImportPageDetailsBomState = {
  bom: null,
  bomRows: [],
  highlightedRowKey: null,
  importId: null,
  loading: false,
  originalBomRows: [],
  pageId: null,
  saving: false
}

function generateClientKey(): string {
  return `row_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
}

function bomItemToRowState(item: BomItemDto): BomRowState {
  return {
    ...item,
    _clientKey: generateClientKey(),
    _lookupPending: false,
    _lookupToken: 0,
    _resolvedToken: 0
  }
}

// One debounced dispatcher per row (keyed by _clientKey) so a burst of edits across
// different rows doesn't collapse onto a single shared timer and silently drop rows.
const lookupDebouncers = new Map<string, (clientKey: string) => void>()

function scheduleLookup(dispatch: any, clientKey: string): void {
  let debounced = lookupDebouncers.get(clientKey)
  if (!debounced) {
    debounced = debounce((key: string) => dispatch('lookupMatchingPart', key), 300)
    lookupDebouncers.set(clientKey, debounced)
  }
  debounced(clientKey)
}

function rowStateToSaveItem(row: BomRowState, displayOrder: number): BomSaveItemDto {
  return {
    displayOrder,
    item: row.item,
    pagePartTagId: row.pagePartTag?.id ?? null,
    pagePartTagValue: row.pagePartTag?.value ?? null,
    partName: row.partName,
    partNumber: row.partNumberCode ? null : row.partNumber,
    partNumberCodeId: row.partNumberCode?.id ?? null,
    quantity: row.quantity,
    supplierId: row.supplierId,
    uom: row.uom,
    supersededPartNumber: row.supersededPartNumber ?? null
  }
}

function validateRow(row: BomRowState): boolean {
  if (!row.supplierId) return false
  if (!row.partNumber?.trim() && !row.partNumberCode) return false

  if (row.matchStatus === 'NEW') {
    if (!row.partName?.trim()) return false
    if (!row.uom?.trim()) return false
  }

  return true
}

const pdfImportPageDetailsBom: Module<PdfImportPageDetailsBomState, any> = {
  namespaced: true,

  state: { ...initialState },

  getters: {
    bomCanSave: (state, getters) => {
      if (!getters.bomIsDirty) return false
      if (state.bomRows.some(row => row._lookupToken !== row._resolvedToken)) return false

      return state.bomRows.every(row => validateRow(row))
    },
    bomIsDirty: (state) => {
      if (state.bomRows.length !== state.originalBomRows.length) return true

      for (let i = 0; i < state.bomRows.length; i++) {
        const current = state.bomRows[i]
        const original = state.originalBomRows[i]

        if (!original) return true

        const fieldsToCompare: (keyof BomItemDto)[] = [
          'item', 'partNumber', 'supplierId', 'partName', 'quantity', 'uom'
        ]

        for (const field of fieldsToCompare) {
          if (current[field] !== original[field]) return true
        }

        if (current.partNumberCode?.id !== original.partNumberCode?.id) return true
        if (current.pagePartTag?.value !== original.pagePartTag?.value) return true
      }

      return false
    },
    bomRows: (state) => state.bomRows,
    defaultSupplierId: (state) => state.bom?.defaultSupplierId ?? 0,
    defaultUom: (state) => state.bom?.defaultUom ?? '',
    highlightedRowKey: (state) => state.highlightedRowKey,
    loading: (state) => state.loading,
    locale: (state) => state.bom?.locale ?? 'en-US',
    pagePartTagName: (state) => state.bom?.pagePartTagName ?? null,
    saving: (state) => state.saving,
    showPagePartTagColumn: (state) => state.bom?.showPagePartTagColumn ?? false
  },

  mutations: {
    ADD_BOM_ROW(state, { row, insertAfterKey }: { row: BomRowState; insertAfterKey?: string }) {
      if (insertAfterKey) {
        const index = state.bomRows.findIndex(r => r._clientKey === insertAfterKey)
        if (index !== -1) {
          state.bomRows.splice(index + 1, 0, row)
          return
        }
      }
      state.bomRows.unshift(row)
    },

    APPLY_PART_LOOKUP(state, { clientKey, result, requestToken }: { clientKey: string; result: PartLookupResponse; requestToken: number }) {
      const row = state.bomRows.find(r => r._clientKey === clientKey)
      if (!row) return

      // A newer edit has already bumped the token past this request's — a fresher
      // lookup is already in flight (or about to be); applying this one would clobber it.
      if (row._lookupToken !== requestToken) return

      row.matchStatus = result.matchStatus
      row._lookupPending = false
      row._resolvedToken = requestToken
      row.partNumberCode = result.partNumberCodeId
        ? { id: result.partNumberCodeId, code: result.partCode ?? '', description: result.partCodeDescription ?? null }
        : null

      if (result.matchStatus === 'EXISTING') {
        row.partName = result.partName
        row.partNameLocked = true
        row.uom = result.uom
        row.uomLocked = true
      } else {
        row.partNameLocked = false
        row.uomLocked = false
      }
    },

    MARK_LOOKUP_RESOLVED(state, { clientKey, requestToken }: { clientKey: string; requestToken: number }) {
      const row = state.bomRows.find(r => r._clientKey === clientKey)
      if (!row || row._lookupToken !== requestToken) return
      row._resolvedToken = requestToken
      row._lookupPending = false
    },

    DELETE_BOM_ROW(state, clientKey: string) {
      const index = state.bomRows.findIndex(r => r._clientKey === clientKey)
      if (index !== -1) {
        state.bomRows.splice(index, 1)
      }
    },

    RESET_STATE(state) {
      Object.assign(state, JSON.parse(JSON.stringify(initialState)))
    },

    REVERT_CHANGES(state) {
      if (state.bom) {
        state.bomRows = state.originalBomRows.map(bomItemToRowState)
      }
    },

    SET_BOM_DATA(state, response: BomViewResponse) {
      state.bom = response
      state.bomRows = response.items.map(bomItemToRowState)
      state.originalBomRows = JSON.parse(JSON.stringify(response.items))
    },

    SET_HIGHLIGHTED_ROW(state, clientKey: string | null) {
      state.highlightedRowKey = clientKey
    },

    SET_IMPORT_ID(state, importId: number) {
      state.importId = importId
    },

    SET_LOADING(state, loading: boolean) {
      state.loading = loading
    },

    SET_PAGE_ID(state, pageId: number) {
      state.pageId = pageId
    },

    SET_ROW_LOOKUP_PENDING(state, { clientKey, pending }: { clientKey: string; pending: boolean }) {
      const row = state.bomRows.find(r => r._clientKey === clientKey)
      if (row) {
        row._lookupPending = pending
      }
    },

    SET_SAVING(state, saving: boolean) {
      state.saving = saving
    },

    UPDATE_BOM_FIELD(state, { clientKey, field, value }: { clientKey: string; field: string; value: any }) {
      const row = state.bomRows.find(r => r._clientKey === clientKey)
      if (!row) return

      if (field === 'pagePartTagValue') {
        row.pagePartTag = { id: row.pagePartTag?.id ?? null, name: row.pagePartTag?.name ?? null, value }
        return
      }

      if (field === 'partNumber') {
        row.partNumber = value
        row.partNumberCode = null
        row.supersededPartNumber = null
        row._lookupToken++
        return
      }

      (row as any)[field] = value

      if (field === 'supplierId') {
        row.supersededPartNumber = null
      }

      if (field === 'supplierId' || (field === 'partName' && row.partNumberCode)) {
        row._lookupToken++
      }
    }
  },

  actions: {
    addBomRow({ state, commit, getters }, insertAfterKey?: string) {
      const newRow: BomRowState = {
        displayOrder: 0,
        id: null,
        item: null,
        matchStatus: 'NEW',
        pagePartTag: null,
        partName: null,
        partNameLocked: false,
        partNumber: null,
        partNumberCode: null,
        quantity: null,
        supplierId: getters.defaultSupplierId,
        uom: getters.defaultUom || null,
        uomLocked: false,
        supersededPartNumber: null,
        _clientKey: generateClientKey(),
        _lookupPending: false,
        _lookupToken: 0,
        _resolvedToken: 0
      }

      const targetKey = insertAfterKey || state.highlightedRowKey || undefined
      commit('ADD_BOM_ROW', { row: newRow, insertAfterKey: targetKey })
      commit('SET_HIGHLIGHTED_ROW', newRow._clientKey)
    },

    deleteBomRow({ commit }, clientKey: string) {
      commit('DELETE_BOM_ROW', clientKey)
      lookupDebouncers.delete(clientKey)
    },

    async initialize({ commit, dispatch }, { importId, pageId }: { importId: number; pageId: number }) {
      commit('RESET_STATE')
      lookupDebouncers.clear()
      commit('SET_IMPORT_ID', importId)
      commit('SET_PAGE_ID', pageId)
      await dispatch('loadBom')
    },

    async loadBom({ state, commit }) {
      if (!state.importId || !state.pageId) return

      try {
        commit('SET_LOADING', true)
        const response = await PdfImportController.getBom(state.importId, state.pageId)
        commit('SET_BOM_DATA', response)
        commit('pdfImportPageDetails/SET_LOCKED', response.locked, { root: true })
      } catch (error: any) {
        throw error
      } finally {
        commit('SET_LOADING', false)
      }
    },

    async lookupMatchingPart({ state, commit }, clientKey: string) {
      const row = state.bomRows.find((r: BomRowState) => r._clientKey === clientKey)
      if (!row) return

      // Every exit path below marks this edit resolved (even when no network call is
      // made) so a row never gets stuck permanently unresolved — validateRow is what
      // actually blocks Save for a missing supplier or blank part number, not this token.
      const requestToken = row._lookupToken

      if (!row.supplierId) {
        commit('MARK_LOOKUP_RESOLVED', { clientKey, requestToken })
        return
      }

      const effectivePartNumber = (row.partNumberCode?.code ?? row.partNumber ?? '').trim()

      if (!effectivePartNumber) {
        commit('MARK_LOOKUP_RESOLVED', { clientKey, requestToken })
        return
      }

      try {
        commit('SET_ROW_LOOKUP_PENDING', { clientKey, pending: true })

        const result = await PdfImportController.searchMatchingPart(
          state.importId!,
          effectivePartNumber,
          row.supplierId,
          row.partName?.trim() || null
        )

        commit('APPLY_PART_LOOKUP', { clientKey, result, requestToken })
      } catch (error: any) {
        console.error('Failed to lookup part:', error)
        if (row._lookupToken === requestToken) {
          commit('SET_ROW_LOOKUP_PENDING', { clientKey, pending: false })
        }
      }
    },

    revertChanges({ commit }) {
      commit('REVERT_CHANGES')
      lookupDebouncers.clear()
    },

    async saveBom({ state, commit, rootState }) {
      if (!state.importId || !state.pageId) return

      try {
        commit('SET_SAVING', true)

        const request: BomSaveRequest = {
          items: state.bomRows.map((row, index) => rowStateToSaveItem(row, index))
        }

        const response = await PdfImportController.saveBom(state.importId, state.pageId, request)
        commit('SET_BOM_DATA', response)

        // Update the BOM item count in the parent store
        commit('pdfImportPageDetails/UPDATE_BOM_ITEM_COUNT', response.bomItemCount, { root: true })

        // Sync overview store with updated BOM count. A successful save means every row
        // passed validation (bomCanSave requires it), so the BOM issue indicator always
        // clears here — the save response has no hasBomIssues field to read this from.
        commit('pdfImportOverview/UPDATE_PAGE_DATA', {
          pageId: state.pageId,
          updates: { bomItemCount: response.bomItemCount, hasBomIssues: false }
        }, { root: true })

        postSuccess({
          title: i18n.global.t('success'),
          text: i18n.global.t('pdfImportPageDetails.notificationSaveSuccess')
        })
      } catch (error: any) {
        const isLocked = error?.response?.status === 423
        postError({
          title: i18n.global.t('error'),
          text: isLocked
            ? i18n.global.t('pdfImport.lockedImportMessage')
            : i18n.global.t('pdfImportPageDetails.notificationSaveFailed')
        })

        throw error
      } finally {
        commit('SET_SAVING', false)
      }
    },

    setHighlightedRow({ state, commit }, clientKey: string | null) {
      const newKey = state.highlightedRowKey === clientKey ? null : clientKey
      commit('SET_HIGHLIGHTED_ROW', newKey)
    },

    updateBomField({ state, commit, dispatch }, { clientKey, field, value }: { clientKey: string; field: string; value: any }) {
      commit('UPDATE_BOM_FIELD', { clientKey, field, value })

      if (field === 'supplierId' || field === 'partNumber') {
        scheduleLookup(dispatch, clientKey)
        return
      }

      // Part Name only participates in match identity for PNC parts
      if (field === 'partName') {
        const row = state.bomRows.find(r => r._clientKey === clientKey)
        if (row?.partNumberCode) {
          scheduleLookup(dispatch, clientKey)
        }
      }
    }
  }
}

export default pdfImportPageDetailsBom
